import { useCallback, useEffect, useState } from 'react';
import * as providerAdminApi from '../../../api/providerAdminApi';
import { useAuth } from '../../../auth/AuthProvider';
import { DataTable } from '../../../components/ui/DataTable';
import { InvitePersonForm } from './InvitePersonForm';
import { PendingInvitationsList } from './PendingInvitationsList';
import { canAccessProviderAdmin } from './providerAdminAccess';

export function PeoplePage() {
  const { session } = useAuth();
  const [query, setQuery] = useState('');
  const [items, setItems] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [orgsError, setOrgsError] = useState(null);
  const [invitesToken, setInvitesToken] = useState(0);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const allowed = canAccessProviderAdmin(session);

  const load = useCallback(async (q) => {
    setLoading(true);
    setError(null);
    try {
      const data = await providerAdminApi.listPeople({ q, page: 1, page_size: 100 });
      setItems(data.items || []);
    } catch (err) {
      setError(err.message || 'Failed to load people');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (allowed) load('');
    else setLoading(false);
  }, [allowed, load]);

  useEffect(() => {
    if (!allowed) return undefined;
    let cancelled = false;
    providerAdminApi
      .listOrganizations({ page: 1, page_size: 100 })
      .then((data) => {
        if (cancelled) return;
        setOrganizations(data?.items || []);
        setOrgsError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setOrganizations([]);
        setOrgsError(err.message || 'Failed to load organizations');
      });
    return () => {
      cancelled = true;
    };
  }, [allowed]);

  if (!allowed) {
    return <p className="text-sm text-eorig-muted">Provider Admin access is required.</p>;
  }

  return (
    <div className="space-y-4">
      <section className="space-y-2">
        <h2 className="text-sm font-semibold text-eorig-ink">Invite a person</h2>
        <p className="text-sm text-eorig-muted">
          Invite someone as a Provider, Originator, Custodian or Investor user. Invite the same email
          more than once to give one person several workspaces.
        </p>
        {orgsError ? (
          <p className="text-sm text-red-600" role="alert">
            {orgsError}
          </p>
        ) : null}
        <InvitePersonForm
          organizations={organizations}
          onInvited={() => {
            setInvitesToken((value) => value + 1);
            return load(query);
          }}
        />
      </section>
      <section className="space-y-2">
        <h2 className="text-sm font-semibold text-eorig-ink">Pending invitations</h2>
        <p className="text-sm text-eorig-muted">
          Resend mints a new one-time link (and re-sends the email when delivery is configured).
        </p>
        <PendingInvitationsList
          organizations={organizations}
          refreshToken={invitesToken}
          onChanged={() => load(query)}
        />
      </section>
      <h2 className="text-sm font-semibold text-eorig-ink">People in your organizations</h2>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          load(query);
        }}
        className="flex gap-2"
      >
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search name or email"
          className="w-full max-w-sm rounded-xl border border-eorig-border px-3 py-2 text-sm"
        />
        <button type="submit" className="rounded-xl bg-[#003A70] px-4 py-2 text-sm font-semibold text-white">
          Search
        </button>
      </form>
      {error ? <p className="text-sm text-red-600">{error}</p> : null}
      {loading ? (
        <p className="text-sm text-eorig-muted">Loading people…</p>
      ) : (
        <DataTable
          columns={[
            { key: 'full_name', header: 'Name' },
            { key: 'email', header: 'Email' },
            { key: 'organization_code', header: 'Org' },
            { key: 'role', header: 'Role' },
            { key: 'status', header: 'Status' },
          ]}
          rows={items}
          rowKey={(row) => row.membership_id}
          emptyMessage="No people in scoped organizations."
        />
      )}
    </div>
  );
}
